/**
 * Copyright (c) 2025 Adaptive Chaos Labs, LLC
 * All rights reserved.
 */

import React from 'react';
import { Box, HStack, Text } from '@chakra-ui/react';
import { FieldHelpTooltip } from '../../ui/FieldHelpTooltip';
import { ControlHover } from '../../ui/ControlHover';
import { IssueDot } from '../editors/ValidationSummary';

/**
 * **A finding about a form field that does not block** (`#1456`): wrong, or probably not meant, and
 * the operator may proceed. Only the two levels a finding on a value can have; an `info` has nothing
 * to say beside a field that its hint does not.
 */
export interface FieldFinding {
  level: 'error' | 'warn';
  /** What is shown beside the field: short enough to sit under a narrow control. */
  message: string;
  /**
   * **The whole sentence, behind a hover** (Todd, 2026-10-06, on `#1573`'s warning): where the reason
   * and the fix do not fit under a field, the field says what is wrong in a few words and the hover says
   * why and what to do. It is in the accessible tree too, as part of the dot's name (`#1276`). Optional
   * with a correct default: most findings are one short sentence, and absent means the message is all
   * there is to say.
   */
  detail?: string;
}

/**
 * **The mark a non-blocking finding takes beside its field** (`#1456`, Todd 2026-09-28): the severity
 * dot rows and section headers already use (`LP9`), in warning or error ink, whose accessible name is
 * the finding's own sentence, and the sentence shown beside it.
 *
 * **No border and no `aria-invalid`.** Those belong to a value that blocks saving, and a field wearing
 * them for something that does not block invites the operator to believe they cannot proceed — or,
 * once they learn they can, to proceed past the one that does. The sentence is hidden from the
 * accessible tree because the dot already carries it as its name; reading it twice is noise.
 */
export const FieldFindingMark: React.FC<{ finding: FieldFinding }> = ({ finding }) => (
  <HStack gap={1.5} align="center" mt={1} data-testid="field-finding" data-severity={finding.level}>
    <IssueDot
      level={finding.level}
      title={finding.detail ? `${finding.message}. ${finding.detail}` : finding.message}
    />
    <ControlHover text={finding.detail ?? null}>
      <Text
        fontSize="xs"
        color={finding.level === 'error' ? 'error.ink' : 'warning.ink'}
        aria-hidden="true"
        textDecoration={finding.detail ? 'underline dotted' : undefined}
        cursor={finding.detail ? 'help' : undefined}
      >
        {finding.message}
      </Text>
    </ControlHover>
  </HStack>
);

export interface FormFieldProps {
  label: string;
  required?: boolean;
  /**
   * **On the surface**, below the control — only what changes what the operator does next: a
   * constraint they would otherwise violate, or a fact they cannot learn by trying (`ST12` item 1).
   */
  hint?: string;
  /**
   * **Behind the `?`** beside the label — what the control is for and why (`ST12` item 1, `3c-ii`).
   * Kept in the accessible tree by `FieldHelpTooltip`, so moving it off the surface removes it for
   * nobody.
   */
  help?: React.ReactNode;
  /**
   * Where this value came from, when the artifact extends another (`P3a`), beside the label. A field
   * with no opinion renders nothing, which is most of them.
   */
  mark?: React.ReactNode;
  /**
   * **Why this value is refused** — a value that blocks saving (`ST12` item 4, `#1456`). The caller
   * decides *when* to pass it: once the operator has been in the box or tried to save, never on a
   * pristine form (`CloneArtifactDialog`'s rule). The control itself carries `aria-invalid`, which is
   * what a refused save looks for to reveal it, and the field draws **the red border** on it (`#1456`):
   * the border is blocking's, and only blocking's.
   *
   * It replaces the hint while present: two sentences under one control, one saying the value is
   * wrong, is the surface arguing with itself.
   */
  error?: string;
  /**
   * **A finding that does not block** (`#1456`), drawn with {@link FieldFindingMark}. Optional because
   * its absence means the correct thing — nothing to say about this value — and the controls that
   * *can* raise one take it as a required prop, so a surface that forgets to pass it cannot compile.
   */
  finding?: FieldFinding | null;
  /** Takes the whole row of a field grid (`LP11`) — a description, an endpoint. */
  wide?: boolean;
  children: React.ReactNode;
}

/**
 * **One form field for every editor** (`3h`, `#1409`/`#1456`): the wizard's phases, the detail page's
 * in-place section editors and the Terms box. They were three components — the wizard's `Field`, the
 * detail page's `EditField` and the Terms box's `Member` — which is how the detail page's Name came to
 * refuse a value with nothing beside the box: only one of the three could say why.
 *
 * Label above the control, as the read view's field grid has it, so a section keeps its layout between
 * reading and editing (`LP14`).
 */
export const FormField: React.FC<FormFieldProps> = ({
  label,
  required,
  hint,
  help,
  mark,
  error,
  finding,
  wide,
  children,
}) => (
  <Box
    gridColumn={wide ? '1 / -1' : undefined}
    minW={0}
    // **The blocking border, drawn once for every control in a field** (`#1456`, Todd 2026-09-28): it
    // is keyed on `aria-invalid`, which only a blocking value sets, so a finding can never wear it and
    // no control has to remember to. A descendant selector outranks the control's own border colour.
    css={{ '& [aria-invalid="true"]': { borderColor: 'var(--chakra-colors-error-border)' } }}
  >
    <HStack gap={2} align="center" mb={1} flexWrap="wrap">
      <Text fontSize="sm" fontWeight="500" color="fg.default">
        {label}
        {required && (
          <Text as="span" color="error.solid" ml={0.5}>
            *
          </Text>
        )}
      </Text>
      {help && <FieldHelpTooltip ariaLabel={`About ${label}`} content={help} />}
      {mark}
    </HStack>
    {children}
    {error ? (
      <Text fontSize="xs" color="error.ink" mt={1} role="alert">
        {error}
      </Text>
    ) : (
      <>
        {finding && <FieldFindingMark finding={finding} />}
        {hint && (
          <Text fontSize="xs" color="fg.muted" mt={1}>
            {hint}
          </Text>
        )}
      </>
    )}
  </Box>
);
